// Verträge -> Bearbeitung -> "Dokumenten-Assistent" (seit 21.10.2026).
//
// Ersetzt das eigene Tool "Bitrix Doku Assistant" (upload.en-power.eu): Ein
// unterschriebenes Dokumentenpaket hochladen (oder den Vertrag aus dem Deal
// nehmen), die Bitrix-KI erkennt Vertrag und Anlagen, der Server schneidet sie
// zu, legt sie in den Deal-Feldern ab und erstellt aus Seite 1 und der
// Unterschriftsseite den Ernsthaftigkeitsnachweis. Die Arbeit laeuft am Server
// im Hintergrund weiter, auch wenn diese Seite zu ist.
//
// Diese Datei haelt nur Liste und Aufrufe. Die Liste wird alle 4 s abgefragt -
// aber nur, solange ein Auftrag wartet oder laeuft UND die Seite sichtbar ist.
// Recht: "Verträge – Dokumenten-Assistent" (dokumentenAssistent), ohne Alias -
// anfangs nur fuer Admins.
//
// Seit 25.10.2026 auch als Reiter im Bitrix-Deal (festeDealId): Formular und Liste gelten
// dann nur fuer diesen Deal; die Liste fragt der Server mit dealId ab (Sichtbarkeit geprueft).
// anmeldeKopf: Anmeldung aus Bitrix (x-bitrix-session) statt des Supabase-Tokens.
// hinweis: Text ueber dem Formular (Bitrix ohne Deal geoeffnet).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { FileStack } from 'lucide-react';
import { T } from '../../../lib/designTokens';
import {
  ABFRAGE_MS, brauchtAbfrage, fehlerText, fuehreListenZusammen, mitAuftrag,
} from '../../../lib/dokumentenAssistent';
import { Hinweis } from './AuftragDetails';
import AuftragFormular from './AuftragFormular';
import AuftragListe from './AuftragListe';

const WEG = '/automation/dokumenten-assistent';
// So lange gilt ein gerade angelegter oder entfernter Auftrag als "lokal
// bekannt", falls eine aeltere Antwort der Liste noch unterwegs ist.
const LOKAL_GUELTIG_MS = 2 * 60 * 1000;

function useSeiteSichtbar() {
  const [sichtbar, setSichtbar] = useState(() => typeof document === 'undefined' || document.visibilityState === 'visible');
  useEffect(() => {
    const melde = () => setSichtbar(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', melde);
    return () => document.removeEventListener('visibilitychange', melde);
  }, []);
  return sichtbar;
}

/** Die ids aus einer Map id -> Zeitpunkt, die noch nicht zu alt sind. */
function frischeIds(karte) {
  const jetzt = Date.now();
  return new Set(Array.from(karte.entries()).filter(([, seit]) => jetzt - seit < LOKAL_GUELTIG_MS).map(([id]) => id));
}

export default function DokumentenAssistent({
  apiBase = '/api', session = null, festeDealId = '', kopfRechts = null, anmeldeKopf = null, hinweis = '',
}) {
  const token = session?.access_token || '';
  const angemeldet = Boolean(anmeldeKopf || token);
  const kopf = useMemo(
    () => anmeldeKopf || (token ? { Authorization: `Bearer ${token}` } : {}),
    [anmeldeKopf, token],
  );
  const sende = useCallback(
    (koerper, timeout = 60000) => axios.post(`${apiBase}${WEG}`, koerper, { headers: kopf, timeout }),
    [apiBase, kopf],
  );

  const [auftraege, setAuftraege] = useState([]);
  const [nachlaufEingerichtet, setNachlaufEingerichtet] = useState(true);
  const [geladen, setGeladen] = useState(false);
  const [laedt, setLaedt] = useState(false);
  const [listeFehler, setListeFehler] = useState('');
  // { [id]: { art: 'wiederholen' | 'entfernen' | '', fehler: '' } }
  const [aktionen, setAktionen] = useState({});

  const laedtGerade = useRef(false);
  // Wird waehrend einer Abfrage neu geladen (nach Anlegen, Wiederholen …),
  // kann deren Antwort schon veraltet sein - dann gleich danach noch einmal.
  const nochmal = useRef(false);
  const ladeNeu = useRef(null);
  const zuletztGeladen = useRef(0);
  const angelegt = useRef(new Map());
  const entfernt = useRef(new Map());
  const sichtbar = useSeiteSichtbar();

  const ladeListe = useCallback(async () => {
    if (!angemeldet) {
      setListeFehler('Ohne Anmeldung lassen sich keine Aufträge laden – bitte neu anmelden.');
      setGeladen(true);
      return;
    }
    if (laedtGerade.current) {
      nochmal.current = true;
      return;
    }
    laedtGerade.current = true;
    setLaedt(true);
    try {
      const { data } = await sende(festeDealId ? { op: 'liste', dealId: festeDealId } : { op: 'liste' }, 30000);
      const vomServer = Array.isArray(data?.auftraege) ? data.auftraege : [];
      const frisch = frischeIds(angelegt.current);
      const weg = frischeIds(entfernt.current);
      setAuftraege((lokal) => fuehreListenZusammen(vomServer, lokal, { frisch, entfernt: weg }));
      setNachlaufEingerichtet(data?.nachlaufEingerichtet !== false);
      setListeFehler('');
    } catch (fehler) {
      setListeFehler(fehlerText(fehler, 'Die Aufträge konnten nicht geladen werden.'));
    } finally {
      laedtGerade.current = false;
      zuletztGeladen.current = Date.now();
      setLaedt(false);
      setGeladen(true);
      if (nochmal.current) {
        nochmal.current = false;
        setTimeout(() => ladeNeu.current?.(), 0);
      }
    }
  }, [angemeldet, festeDealId, sende]);

  useEffect(() => {
    ladeNeu.current = ladeListe;
    ladeListe();
  }, [ladeListe]);

  // Abfrage nur, solange etwas offen ist und die Seite sichtbar. Wird sie
  // wieder sichtbar, gleich einmal abfragen statt 4 s zu warten.
  const offen = brauchtAbfrage(auftraege);
  useEffect(() => {
    if (!offen || !sichtbar) return undefined;
    if (Date.now() - zuletztGeladen.current >= ABFRAGE_MS) ladeListe();
    // Laeuft die vorige Abfrage noch, faellt dieser Takt aus (kein Stau).
    const uhr = setInterval(() => { if (!laedtGerade.current) ladeListe(); }, ABFRAGE_MS);
    return () => clearInterval(uhr);
  }, [ladeListe, offen, sichtbar]);

  const setzeAktion = useCallback((id, stand) => {
    setAktionen((alt) => ({ ...alt, [id]: { art: '', fehler: '', ...stand } }));
  }, []);

  const auftragAngelegt = useCallback((auftrag) => {
    angelegt.current.set(auftrag.id, Date.now());
    setAuftraege((alt) => mitAuftrag(alt, auftrag));
    ladeListe();
  }, [ladeListe]);

  const wiederholen = useCallback(async (id) => {
    setzeAktion(id, { art: 'wiederholen' });
    try {
      const { data } = await sende({ op: 'wiederholen', id }, 60000);
      if (data?.auftrag?.id) {
        angelegt.current.set(data.auftrag.id, Date.now());
        setAuftraege((alt) => mitAuftrag(alt, data.auftrag));
      }
      setzeAktion(id, {});
      ladeListe();
    } catch (fehler) {
      setzeAktion(id, { fehler: fehlerText(fehler, 'Der Auftrag konnte nicht neu eingereiht werden.') });
    }
  }, [ladeListe, sende, setzeAktion]);

  const entfernen = useCallback(async (id) => {
    setzeAktion(id, { art: 'entfernen' });
    try {
      await sende({ op: 'entfernen', id }, 30000);
      entfernt.current.set(id, Date.now());
      setAuftraege((alt) => alt.filter((a) => a.id !== id));
      setAktionen((alt) => Object.fromEntries(Object.entries(alt).filter(([schluessel]) => schluessel !== id)));
    } catch (fehler) {
      setzeAktion(id, { fehler: fehlerText(fehler, 'Der Auftrag konnte nicht entfernt werden.') });
    }
  }, [sende, setzeAktion]);

  return (
    <div style={{ minHeight: '100%', background: T.grund }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '12px 20px',
        background: T.flaeche, borderBottom: `1px solid ${T.randWeich}`,
      }}>
        <FileStack size={16} color={T.navy} />
        <span style={{ fontSize: T.tTitel, fontWeight: 700, color: T.navy, letterSpacing: T.spurTitel }}>
          Dokumenten-Assistent{festeDealId ? ` · Deal ${festeDealId}` : ''}
        </span>
        <span style={{ flex: '1 1 260px', fontSize: T.tMeta, color: T.muted }}>
          Unterschriebene Unterlagen erkennen, zuschneiden und im Deal ablegen – mit Ernsthaftigkeitsnachweis.
        </span>
        {kopfRechts}
      </div>

      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '18px 20px 40px', display: 'grid', gap: 22 }}>
        {hinweis ? <Hinweis ton="info">{hinweis}</Hinweis> : null}
        <AuftragFormular sende={sende} onAngelegt={auftragAngelegt} festeDealId={festeDealId} />
        <AuftragListe
          festeDealId={festeDealId}
          auftraege={auftraege}
          geladen={geladen}
          laedt={laedt}
          fehler={listeFehler}
          nachlaufEingerichtet={nachlaufEingerichtet}
          aktionen={aktionen}
          onAktualisieren={ladeListe}
          onWiederholen={wiederholen}
          onEntfernen={entfernen}
        />
      </div>
    </div>
  );
}
